import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Download,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Search,
  ArrowLeft,
  Wallet,
  Users,
  CheckCircle,
  XCircle,
  Building,
} from "lucide-react";
import * as XLSX from "xlsx";

export const DriverBankDetailsReport = ({ onBack }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total_count: 0,
    total_pages: 1
  });
  const [summary, setSummary] = useState({ total_drivers: 0, drivers_with_bank_details: 0 });
  
  // Filters
  const [filters, setFilters] = useState(null);
  const [cityFilter, setCityFilter] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch filter options
  useEffect(() => {
    fetchFilters();
  }, []);

  // Fetch data when filters change
  useEffect(() => {
    fetchDrivers();
  }, [cityFilter, searchQuery, pagination.page]);

  const fetchFilters = async () => {
    try {
      const res = await axios.get(`${API}/reports/driver-bank-details/filters?admin_id=${user?.id}`);
      setFilters(res.data);
    } catch (err) {
      console.error("Failed to fetch filters:", err);
    }
  };

  const fetchDrivers = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("admin_id", user.id);
      params.append("page", pagination.page.toString());
      params.append("limit", "50");
      if (cityFilter !== "all") params.append("city_id", cityFilter);
      if (searchQuery) params.append("search", searchQuery);
      
      const res = await axios.get(`${API}/reports/driver-bank-details?${params}`);
      setDrivers(res.data.rows || []);
      setPagination(res.data.pagination || pagination);
      setSummary(res.data.summary || { total_drivers: 0, drivers_with_bank_details: 0 });
    } catch (err) {
      toast.error("Failed to load driver bank details");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setSearchQuery(searchInput);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.total_pages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  const exportToExcel = () => {
    if (!drivers.length) {
      toast.error("No data to export");
      return;
    }
    
    const exportData = drivers.map(d => ({
      "City": d.city,
      "Rider Name": d.rider_name,
      "Phone": d.phone,
      "Vehicle Type": d.vehicle_type,
      "Bank Name": d.bank_name,
      "Account Holder": d.account_holder_name,
      "Account Number": d.account_number,
      "IFSC Code": d.ifsc_code,
      "Mobile (Bank)": d.mobile_number,
      "Email": d.email,
      "Has Bank Details": d.has_bank_details ? "Yes" : "No"
    }));
    
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Driver Bank Details");
    XLSX.writeFile(wb, `Driver_Bank_Details_Report.xlsx`);
    toast.success("Report exported successfully");
  };

  const maskAccountNumber = (accNum) => {
    if (!accNum || accNum === "-") return "-";
    if (accNum.length <= 4) return accNum;
    return "****" + accNum.slice(-4);
  };

  return (
    <div className="space-y-6" data-testid="driver-bank-details-report">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div>
            <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Building className="w-6 h-6 text-primary" />
              Driver Bank Details Report
            </h2>
            <p className="text-muted-foreground text-sm mt-1">
              View all drivers and their bank details for payments
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchDrivers} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={exportToExcel} data-testid="export-btn">
            <Download className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Drivers</p>
                <p className="text-3xl font-bold">{summary.total_drivers}</p>
              </div>
              <Users className="w-10 h-10 text-primary opacity-20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">With Bank Details</p>
                <p className="text-3xl font-bold text-green-600">{summary.drivers_with_bank_details}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {summary.total_drivers > 0 
                    ? `${Math.round((summary.drivers_with_bank_details / summary.total_drivers) * 100)}% of drivers`
                    : "0%"}
                </p>
              </div>
              <Wallet className="w-10 h-10 text-green-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Search className="w-5 h-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* City Filter */}
            <div>
              <label className="text-sm font-medium mb-1 block">City</label>
              <Select 
                value={cityFilter} 
                onValueChange={(v) => {
                  setCityFilter(v);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
              >
                <SelectTrigger data-testid="city-filter">
                  <SelectValue placeholder="All Cities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cities</SelectItem>
                  {filters?.cities?.map(city => (
                    <SelectItem key={city.id} value={city.id}>{city.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Search */}
            <div className="md:col-span-2">
              <label className="text-sm font-medium mb-1 block">Search Rider</label>
              <div className="flex gap-2">
                <Input
                  placeholder="Name or phone number..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  data-testid="rider-search"
                />
                <Button variant="outline" onClick={handleSearch}>
                  <Search className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>City</TableHead>
                  <TableHead>Rider Name</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Bank Status</TableHead>
                  <TableHead>Bank Name</TableHead>
                  <TableHead>Account Holder</TableHead>
                  <TableHead>Account Number</TableHead>
                  <TableHead>IFSC Code</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto text-primary" />
                      <p className="text-muted-foreground mt-2">Loading...</p>
                    </TableCell>
                  </TableRow>
                ) : drivers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No drivers found for the selected filters
                    </TableCell>
                  </TableRow>
                ) : (
                  drivers.map((driver) => (
                    <TableRow key={driver.driver_id} data-testid={`driver-row-${driver.driver_id}`}>
                      <TableCell className="font-medium">{driver.city}</TableCell>
                      <TableCell>{driver.rider_name}</TableCell>
                      <TableCell>{driver.phone}</TableCell>
                      <TableCell>
                        {driver.has_bank_details ? (
                          <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                            <CheckCircle className="w-3 h-3" />
                            Added
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs bg-red-100 text-red-700 px-2 py-1 rounded">
                            <XCircle className="w-3 h-3" />
                            Missing
                          </span>
                        )}
                      </TableCell>
                      <TableCell>{driver.bank_name}</TableCell>
                      <TableCell>{driver.account_holder_name}</TableCell>
                      <TableCell className="font-mono">{maskAccountNumber(driver.account_number)}</TableCell>
                      <TableCell className="font-mono">{driver.ifsc_code}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <p className="text-sm text-muted-foreground">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to{" "}
                {Math.min(pagination.page * pagination.limit, pagination.total_count)} of{" "}
                {pagination.total_count} drivers
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  data-testid="prev-page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm">
                  Page {pagination.page} of {pagination.total_pages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.total_pages}
                  data-testid="next-page"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
